/**
 * Générateur — Comparer des résultats d'une expérience aléatoire répétée à une probabilité calculée
 * Attendu ID : 681 | Niveau : 6ème | Domaine : Organisation et gestion de données et probabilités
 *
 * Templates exercices (niveau didactique) :
 *   - comparer_frequence_probabilite         (qcm_unique, texte, tableau_vf, texte_trou_saisie)
 *   - estimer_succes_grand_nombre            (qcm_unique, texte)
 *   - interpreter_graphique_stabilisation    (qcm_unique, texte, tableau_vf)
 *
 * Labels de référence :
 *   - Comparer fréquence observée et probabilité théorique
 *   - Estimer un nombre de succès sur un grand nombre de répétitions
 *   - Interpréter un graphique de stabilisation de fréquence
 *
 * Variantes estimées : ~1500+
 */

import {
    randomChoice, randomChoiceMultiple, randInt, shuffle,
    makeQcm, makeSaisie, makeVraiFaux, makeTexteTrou,
} from '../../../utils';

// ─── Constantes ───
const ATTENDU_ID = 681;
const opts = (label) => ({ niveau: '6eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_FREQ       = 'Comparer fréquence observée et probabilité théorique';
const L_ESTIMER    = 'Estimer un nombre de succès sur un grand nombre de répétitions';
const L_GRAPHIQUE  = 'Interpréter un graphique de stabilisation de fréquence';

// ─── Helpers ───

const pgcd = (a, b) => (b === 0 ? a : pgcd(b, a % b));
const simplifier = (num, den) => {
    const g = pgcd(num, den);
    return { num: num / g, den: den / g };
};
const fractionStr = (num, den) => {
    const s = simplifier(num, den);
    return `${s.num}/${s.den}`;
};
const decimalStr = (num, den, precision = 3) => {
    const val = num / den;
    return val.toFixed(precision).replace(/\.?0+$/, '').replace('.', ',');
};
const pourcentageStr = (num, den) => {
    const pct = (num / den) * 100;
    if (Math.abs(pct - Math.round(pct)) < 0.01) return `${Math.round(pct)} %`;
    return `${pct.toFixed(1).replace('.', ',')} %`;
};

// ─── Pools ───

const PRENOMS = [
    { n: 'Maxence', fem: false }, { n: 'Amina', fem: true }, { n: 'Lina', fem: true },
    { n: 'Hugo', fem: false }, { n: 'Léa', fem: true }, { n: 'Noa', fem: false },
    { n: 'Mila', fem: true }, { n: 'Ethan', fem: false }, { n: 'Camille', fem: true },
    { n: 'Sacha', fem: false }, { n: 'Inès', fem: true }, { n: 'Nora', fem: true },
];

const EXPERIENCES = [
    { desc: 'une pièce équilibrée', evt: 'Pile', proba: { num: 1, den: 2 } },
    { desc: 'une pièce équilibrée', evt: 'Face', proba: { num: 1, den: 2 } },
    { desc: 'un dé équilibré à 6 faces', evt: 'le chiffre 6', proba: { num: 1, den: 6 } },
    { desc: 'un dé équilibré à 6 faces', evt: 'un nombre pair', proba: { num: 1, den: 2 } },
    { desc: 'un dé équilibré à 6 faces', evt: 'un multiple de 3', proba: { num: 1, den: 3 } },
    { desc: 'un dé équilibré à 10 faces', evt: 'le chiffre 1', proba: { num: 1, den: 10 } },
    { desc: 'une roue de loterie à 4 secteurs identiques dont 1 rouge', evt: 'un secteur rouge', proba: { num: 1, den: 4 } },
    { desc: 'une roue de loterie à 5 secteurs identiques dont 2 rouges', evt: 'un secteur rouge', proba: { num: 2, den: 5 } },
    // Probabilités > 1/2 (revue 2026-08-05) : sans elles, la fréquence était
    // toujours sous le complément → au QCM, la bonne réponse était le min des
    // choix dans 81 % des tirages.
    { desc: 'un dé équilibré à 6 faces', evt: 'un nombre de 1 à 5', proba: { num: 5, den: 6 } },
    { desc: 'un dé équilibré à 6 faces', evt: 'un nombre supérieur ou égal à 3', proba: { num: 2, den: 3 } },
    { desc: 'une roue de loterie à 4 secteurs identiques dont 3 bleus', evt: 'un secteur bleu', proba: { num: 3, den: 4 } },
    { desc: 'une roue de loterie à 5 secteurs identiques dont 3 verts', evt: 'un secteur vert', proba: { num: 3, den: 5 } },
];

// Élision devant l'événement (revue 2026-08-05) : « d'apparition de un
// multiple de 3 » et « de le chiffre 6 » étaient fautifs.
const deEvt = (evt) => {
    if (evt.startsWith('le ')) return `du ${evt.slice(3)}`;
    if (evt.startsWith('un') || evt.startsWith('une')) return `d'${evt}`;
    return `de ${evt}`;
};

const NB_LANCERS = [20, 50, 100, 200, 500, 1000, 2000];
const NB_LANCERS_GRAND = [500, 1000, 2000, 5000, 10000];

// ─── Template 1 : comparer_frequence_probabilite ───

const genFreqQcm = () => {
    const exp = randomChoice(EXPERIENCES);
    const prenom = randomChoice(PRENOMS);
    const N = randomChoice(NB_LANCERS);
    // K cohérent avec proba théorique, avec variation raisonnable
    const probaVal = exp.proba.num / exp.proba.den;
    const variation = N < 100 ? 0.4 : 0.2;
    const minK = Math.max(1, Math.round(N * probaVal * (1 - variation)));
    const maxK = Math.min(N - 1, Math.round(N * probaVal * (1 + variation)));
    const K = randInt(minK, maxK);

    // Bonne réponse : K/N avec décimal. Précision 4 (revue 2026-08-05) : les
    // pools montent à 2000 lancers (pas de 0,0005) — à 3 décimales,
    // « 923/2000 = 0,462 » affichait une égalité FAUSSE. Tous les N du pool
    // divisent 10^4, l'écriture à 4 décimales est donc toujours exacte.
    const dec = decimalStr(K, N, 4);
    const bonne = `${K}/${N} = ${dec}`;

    // Distracteurs : inversion, complément, effectif brut — dédupliqués
    // Set revu 2026-08-05 (doctrine 680) : complément + UN proche à pile ou
    // face (ratio succès/échec, ou l'erreur de virgule qui passe SOUS la
    // bonne) + UN SEUL hors-bornes — avant, deux géants (> 1) systématiques.
    const distracteursPool = [
        `${N - K}/${N} = ${decimalStr(N - K, N, 4)}`,                       // complément
        Math.random() < 0.5
            ? `${K}/${N - K}`                                               // ratio succès/échec
            : `${K}/${N} = ${decimalStr(K, N * 10, 5)}`,                    // erreur de virgule
        Math.random() < 0.5 ? `${N}/${K} ≈ ${decimalStr(N, K, 2)}` : `${K}`, // hors-bornes, un seul
        `${K}/${N - K}`,                                                     // secours
        `${N}`,                                                              // secours
    ];
    const distUniques = [];
    for (const d of distracteursPool) {
        if (d !== bonne && !distUniques.includes(d)) distUniques.push(d);
        if (distUniques.length >= 3) break;
    }
    const choix = shuffle([bonne, ...distUniques.slice(0, 3)]);
    const texte = `<p>${prenom.n} a lancé ${exp.desc} <strong>${N} fois</strong> et a obtenu <strong>${K} fois ${exp.evt}</strong>.</p><p>Quelle est la fréquence observée d'apparition ${deEvt(exp.evt)} ?</p>`;
    const payload = makeQcm(choix, bonne, texte, opts(L_FREQ));
    payload._meta.generatorKey = 'comparer_frequence_probabilite';
    return payload;
};

const genFreqTexte = () => {
    const exp = randomChoice(EXPERIENCES);
    const prenom = randomChoice(PRENOMS);
    const N = randomChoice([50, 100, 200, 500]);
    const probaVal = exp.proba.num / exp.proba.den;
    const K = randInt(
        Math.max(1, Math.round(N * probaVal * 0.7)),
        Math.min(N - 1, Math.round(N * probaVal * 1.3)),
    );
    const bonne = decimalStr(K, N);

    const texte = `<p>${prenom.n} a lancé ${exp.desc} ${N} fois et a obtenu <strong>${K} fois ${exp.evt}</strong>.</p><p>Calcule la fréquence d'apparition ${deEvt(exp.evt)}. Donne ta réponse sous forme de nombre décimal.</p>`;
    const payload = makeSaisie(bonne, texte, opts(L_FREQ));
    // Calculatrice AUTORISÉE (revue 2026-08-05) : produire 179/500 en décimal
    // dépasse le calcul mental confortable de 6ème.
    payload.contenu = { ...payload.contenu, calculatrice: true };
    payload._meta.generatorKey = 'comparer_frequence_probabilite';
    return payload;
};

const genFreqTableauVf = () => {
    const exp = randomChoice(EXPERIENCES);
    const prenom = randomChoice(PRENOMS);
    const N = randomChoice([500, 1000, 2000]);
    const probaVal = exp.proba.num / exp.proba.den;
    const K = randInt(
        Math.max(1, Math.round(N * probaVal * 0.92)),
        Math.min(N - 1, Math.round(N * probaVal * 1.08)),
    );
    // Précision 4 (revue 2026-08-05) : ce format tire dans {500, 1000, 2000} —
    // à 3 décimales, « 1541/2000 = 0,77 » était une ligne « vraie »… fausse.
    const dec = decimalStr(K, N, 4);
    const probaStr = `${exp.proba.num}/${exp.proba.den}`;

    // Viviers + quota VARIABLE (revue 2026-08-05, avant : 3 V / 2 F figés).
    const viverVraies = [
        `La fréquence observée est ${K}/${N} = ${dec}`,
        `La fréquence observée est proche de la probabilité théorique ${probaStr}`,
        `Sur un nombre encore plus grand de lancers, la fréquence se rapprocherait davantage de ${probaStr}`,
    ];
    const viverFausses = [
        `Sur ${N} lancers, on obtient toujours exactement ${Math.round(N * probaVal)} fois ${exp.evt}`,
        `La probabilité théorique d'obtenir ${exp.evt} vaut ${K}/${N}`,
        `La fréquence observée est ${N}/${K}`,
        `La fréquence observée est ${K}`,
    ];
    const nbVraies = randInt(2, 3);
    const vraies = randomChoiceMultiple(viverVraies, nbVraies);
    const fausses = randomChoiceMultiple(viverFausses, 5 - nbVraies);

    const lignes = [];
    const reponses = [];
    vraies.forEach(v => { lignes.push(v); reponses.push(0); });
    fausses.forEach(f => { lignes.push(f); reponses.push(1); });
    const indices = shuffle(lignes.map((_, i) => i));
    const ligneMel = indices.map(i => lignes[i]);
    const reponsesMel = indices.map(i => reponses[i]);

    const texte = `<p>${prenom.n} a lancé ${exp.desc} <strong>${N} fois</strong> et a obtenu <strong>${K} fois ${exp.evt}</strong>. La probabilité théorique d'obtenir ${exp.evt} est <strong>${probaStr}</strong>. Indique si chaque affirmation est vraie ou fausse.</p>{{element:0}}`;
    const payload = makeVraiFaux(ligneMel, reponsesMel, texte, opts(L_FREQ));
    // Calculatrice AUTORISÉE (revue 2026-08-05) : vérifier « 800/2000 = 0,4 »
    // exige de refaire la division.
    payload.contenu = { ...payload.contenu, calculatrice: true };
    payload._meta.generatorKey = 'comparer_frequence_probabilite';
    return payload;
};

const genFreqTexteTrouSaisie = () => {
    const exp = randomChoice(EXPERIENCES);
    const prenom = randomChoice(PRENOMS);
    const N = randomChoice([50, 100, 200, 500]);
    const probaVal = exp.proba.num / exp.proba.den;
    const K = randInt(
        Math.max(1, Math.round(N * probaVal * 0.8)),
        Math.min(N - 1, Math.round(N * probaVal * 1.2)),
    );
    const dec = decimalStr(K, N);

    const phrase = `Sur {{${N}}} lancers, la fréquence d'apparition ${deEvt(exp.evt)} est {{${K}}}/{{${N}}} = {{${dec}}}.`;
    const payload = makeTexteTrou(
        phrase,
        `<p>${prenom.n} a lancé ${exp.desc} ${N} fois et a obtenu ${K} fois ${exp.evt}. Complète la phrase avec les bons nombres.</p>`,
        { ...opts(L_FREQ), mode: 'saisie' },
    );
    // Calculatrice AUTORISÉE (revue 2026-08-05) : le trou final demande la
    // division K/N en décimal.
    payload.contenu = { ...payload.contenu, calculatrice: true };
    payload._meta.generatorKey = 'comparer_frequence_probabilite';
    return payload;
};

// ─── Template 2 : estimer_succes_grand_nombre ───

const genEstimerQcm = () => {
    const exp = randomChoice(EXPERIENCES);
    // Assurer que N × proba donne un entier. Multiplicateur 2..50 (revue
    // 2026-08-05, avant : 2..20 → 228 énoncés possibles seulement) : des
    // lancers jusqu'à ~300, cohérent avec le « grand nombre » du thème.
    const targetProduct = randInt(2, 50);
    const N = exp.proba.den * targetProduct;
    const esperance = exp.proba.num * targetProduct;

    const bonne = `Environ ${esperance} fois`;
    const distracteursPool = [
        `Exactement ${esperance} fois`,
        `Environ ${Math.floor(N / 2)} fois`,
        `Environ ${exp.proba.den} fois`,
        `Environ ${N - esperance} fois`,     // complément
        `Environ ${N} fois`,                 // total
    ];
    const distUniques = [];
    for (const d of distracteursPool) {
        if (d !== bonne && !distUniques.includes(d)) distUniques.push(d);
        if (distUniques.length >= 3) break;
    }
    const choix = shuffle([bonne, ...distUniques.slice(0, 3)]);
    const texte = `<p>On lance <strong>${N} fois</strong> ${exp.desc}. <strong>Environ</strong> combien de fois peut-on s'attendre à obtenir ${exp.evt} ?</p>`;
    const payload = makeQcm(choix, bonne, texte, opts(L_ESTIMER));
    // Calculatrice AUTORISÉE (décision utilisateur 2026-08-05).
    payload.contenu = { ...payload.contenu, calculatrice: true };
    payload._meta.generatorKey = 'estimer_succes_grand_nombre';
    return payload;
};

const FORMULATIONS_ESTIMER = [
    (p, N, desc, evt) => `<p>${p.n} répète ${N} fois l'expérience suivante : lancer ${desc}.</p><p>Environ combien de fois peut-on s'attendre à obtenir ${evt} ?</p>`,
    (p, N, desc, evt) => `<p>On répète ${N} fois l'expérience suivante : lancer ${desc}.</p><p>D'après ${p.n}, environ combien de fois va-t-on obtenir ${evt} ?</p>`,
    // « Estimation raisonnable » réécrite (revue 2026-08-05) : la consigne
    // promettait de la souplesse alors que seul N × p est accepté — l'élève
    // répondant 24 pour 25 était compté faux sur un contrat qu'il respectait.
    (p, N, desc, evt) => `<p>${p.n} compte le nombre de fois où ${p.fem ? 'elle obtient' : 'il obtient'} ${evt} en lançant ${N} fois ${desc}.</p><p>Environ combien de fois peut-${p.fem ? 'elle' : 'il'} s'y attendre ?</p>`,
    // Élision (revue 2026-08-05) : toutes les expériences commencent par
    // « un/une » — « lancers de un dé » était fautif.
    (p, N, desc, evt) => `<p>Sur ${N} lancers d'${desc}, environ combien de fois ${p.n} peut-${p.fem ? 'elle' : 'il'} s'attendre à obtenir ${evt} ?</p>`,
];

const genEstimerTexte = () => {
    const exp = randomChoice(EXPERIENCES);
    const targetProduct = randInt(3, 30);
    const N = exp.proba.den * targetProduct;
    const esperance = exp.proba.num * targetProduct;
    const prenom = randomChoice(PRENOMS);
    const formulation = randomChoice(FORMULATIONS_ESTIMER);
    const texte = formulation(prenom, N, exp.desc, exp.evt);
    const payload = makeSaisie(String(esperance), texte, { ...opts(L_ESTIMER), suffixe: 'fois' });
    // Calculatrice AUTORISÉE (décision utilisateur 2026-08-05).
    payload.contenu = { ...payload.contenu, calculatrice: true };
    payload._meta.generatorKey = 'estimer_succes_grand_nombre';
    return payload;
};

// ─── Template 3 : interpreter_graphique_stabilisation ───

// Génère un SVG de graphique de stabilisation avec une courbe simulée qui se
// stabilise vers cible.val. L'étiquette de la ligne pointillée est cible.str —
// MÊME source de vérité que la bonne réponse (jamais de re-formatage toFixed
// qui pourrait diverger, ex. 0,125 affiché « 0,13 »).
const svgGraphiqueStab = (cible) => {
    const probaCible = cible.val;
    const w = 460, h = 280;
    const margL = 50, margR = 20, margT = 40, margB = 40;
    const xAxisStart = margL, xAxisEnd = w - margR;
    const yAxisStart = h - margB, yAxisEnd = margT;
    const yCible = yAxisStart - probaCible * (yAxisStart - yAxisEnd);

    // Générer une courbe qui converge vers probaCible
    // 13 points progressifs (x de 55 à 440, y variable qui se stabilise)
    const pts = [];
    const xValues = [55, 70, 85, 100, 120, 145, 170, 200, 240, 290, 340, 400, 440];
    for (let i = 0; i < xValues.length; i++) {
        const t = i / (xValues.length - 1); // 0..1
        const noise = (1 - t) * 0.35 + 0.02; // décroissance du bruit
        // Zigzag GARANTI sur les 4 premiers points (revue 2026-08-05) :
        // alternance au-dessus/en dessous avec amplitude ≥ 0,08 — sinon le
        // hasard produisait parfois un début plat sous une affirmation
        // « varie beaucoup » à juger Vrai (2,4 % des tirages).
        const rand = i < 4
            ? (i % 2 === 0 ? 1 : -1) * (0.08 + Math.random() * Math.max(0.02, noise - 0.08))
            : (Math.random() * 2 - 1) * noise;
        const y = probaCible + rand;
        const yClamped = Math.max(0.02, Math.min(0.98, y));
        const yPx = yAxisStart - yClamped * (yAxisStart - yAxisEnd);
        pts.push(`${xValues[i]},${yPx.toFixed(1)}`);
    }
    const polyline = pts.join(' ');

    const cibleLabel = cible.str;

    return `<svg viewBox="0 0 ${w} ${h}" xmlns="http://www.w3.org/2000/svg" style="max-width:${w}px;display:block;margin:8px auto">
<line x1="${xAxisStart}" y1="${yAxisStart}" x2="${xAxisEnd}" y2="${yAxisStart}" stroke="#1e293b" stroke-width="1.5"/>
<line x1="${xAxisStart}" y1="${yAxisEnd}" x2="${xAxisStart}" y2="${yAxisStart}" stroke="#1e293b" stroke-width="1.5"/>
<polygon points="${xAxisEnd},${yAxisStart} ${xAxisEnd - 8},${yAxisStart - 4} ${xAxisEnd - 8},${yAxisStart + 4}" fill="#1e293b"/>
<polygon points="${xAxisStart},${yAxisEnd} ${xAxisStart - 4},${yAxisEnd + 8} ${xAxisStart + 4},${yAxisEnd + 8}" fill="#1e293b"/>
<line x1="${xAxisStart}" y1="${yCible.toFixed(1)}" x2="${xAxisEnd}" y2="${yCible.toFixed(1)}" stroke="#94a3b8" stroke-width="1" stroke-dasharray="4 3"/>
<text x="${xAxisStart - 5}" y="${(yCible + 4).toFixed(1)}" text-anchor="end" font-size="12" fill="#475569">${cibleLabel}</text>
<text x="${xAxisStart - 5}" y="${yAxisEnd + 4}" text-anchor="end" font-size="12" fill="#475569">1</text>
<text x="${xAxisStart - 5}" y="${yAxisStart + 4}" text-anchor="end" font-size="12" fill="#475569">0</text>
<text x="${xAxisStart}" y="${yAxisStart + 22}" text-anchor="middle" font-size="12" fill="#475569">0</text>
<text x="${(xAxisStart + xAxisEnd) / 2}" y="${yAxisStart + 22}" text-anchor="middle" font-size="12" fill="#475569">500</text>
<text x="${xAxisEnd}" y="${yAxisStart + 22}" text-anchor="middle" font-size="12" fill="#475569">1000</text>
<text x="${(xAxisStart + xAxisEnd) / 2}" y="${h - 2}" text-anchor="middle" font-size="13" fill="#1e293b">Nombre de lancers</text>
<text x="15" y="${(yAxisEnd + yAxisStart) / 2}" text-anchor="middle" font-size="13" fill="#1e293b" transform="rotate(-90 15 ${(yAxisEnd + yAxisStart) / 2})">Fréquence</text>
<polyline points="${polyline}" fill="none" stroke="#E24B4A" stroke-width="2"/>
</svg>`;
};

// Cibles élargies (enrichissement 2026-07-04) : 20 probabilités « lisibles »
// (moitiés, tiers, quarts, cinquièmes, sixièmes, huitièmes, dixièmes).
// str = étiquette du graphique ET bonne réponse (source de vérité unique).
const PROBA_CIBLES_GRAPH = [
    { val: 0.05, str: '0,05', frac: '1/20' },
    { val: 0.1, str: '0,1', frac: '1/10' },
    // +7 cibles (revue 2026-08-05) : 20 → 27 valeurs lisibles.
    { val: 0.15, str: '0,15', frac: '3/20' },
    { val: 0.125, str: '0,125', frac: '1/8' },
    { val: 0.17, str: '0,17', frac: '1/6' },
    { val: 0.2, str: '0,2', frac: '1/5' },
    { val: 0.25, str: '0,25', frac: '1/4' },
    { val: 0.3, str: '0,3', frac: '3/10' },
    { val: 0.33, str: '0,33', frac: '1/3' },
    { val: 0.35, str: '0,35', frac: '7/20' },
    { val: 0.375, str: '0,375', frac: '3/8' },
    { val: 0.4, str: '0,4', frac: '2/5' },
    { val: 0.45, str: '0,45', frac: '9/20' },
    { val: 0.5, str: '0,5', frac: '1/2' },
    { val: 0.55, str: '0,55', frac: '11/20' },
    { val: 0.6, str: '0,6', frac: '3/5' },
    { val: 0.625, str: '0,625', frac: '5/8' },
    { val: 0.65, str: '0,65', frac: '13/20' },
    { val: 0.67, str: '0,67', frac: '2/3' },
    { val: 0.7, str: '0,7', frac: '7/10' },
    { val: 0.75, str: '0,75', frac: '3/4' },
    { val: 0.8, str: '0,8', frac: '4/5' },
    { val: 0.83, str: '0,83', frac: '5/6' },
    { val: 0.85, str: '0,85', frac: '17/20' },
    { val: 0.875, str: '0,875', frac: '7/8' },
    { val: 0.9, str: '0,9', frac: '9/10' },
    { val: 0.95, str: '0,95', frac: '19/20' },
];

/** Autres cibles ÉLOIGNÉES d'au moins 0,08 (pièges discernables sur le graphique). */
const autresCiblesEloignees = (cible) =>
    shuffle(PROBA_CIBLES_GRAPH.filter(c => Math.abs(c.val - cible.val) >= 0.08));

// Format QCM RETIRÉ (décision utilisateur 2026-07-04) : la valeur de
// stabilisation est affichée sur le graphique (étiquette de la ligne
// pointillée) — un QCM qui la repropose parmi 4 choix ne teste rien.
// La Saisie (lire et transcrire) et le V/F (comprendre la stabilisation)
// restent les deux formats de la carte.

const genGraphiqueTexte = () => {
    const cible = randomChoice(PROBA_CIBLES_GRAPH);
    const svg = svgGraphiqueStab(cible);

    const texte = `<p>Le graphique ci-dessous représente la fréquence d'apparition d'un événement au fil des tirages. Quelle est approximativement la probabilité théorique de cet événement ?</p>${svg}<p>Écris ta réponse en écriture décimale, comme sur le graphique (ex. : 0,5).</p>`;
    const payload = makeSaisie(cible.str, texte, opts(L_GRAPHIQUE));
    payload._meta.generatorKey = 'interpreter_graphique_stabilisation';
    return payload;
};

const genGraphiqueTableauVf = () => {
    const cible = randomChoice(PROBA_CIBLES_GRAPH);
    const svg = svgGraphiqueStab(cible);
    const autreCible = autresCiblesEloignees(cible)[0];

    // Viviers + quota VARIABLE (revue 2026-08-05, avant : 3 V / 2 F figés).
    const viverVraies = [
        `La fréquence se stabilise autour de ${cible.str}`,
        `Plus il y a de lancers, plus la fréquence se rapproche de ${cible.str}`,
        'Sur les 20 premiers lancers, la fréquence varie beaucoup',
    ];
    const viverFausses = [
        `Après 1000 lancers, la fréquence vaut exactement ${cible.str}`,
        `La probabilité théorique de cet événement est ${autreCible.frac}`,
        'Dès les premiers lancers, la fréquence est déjà stabilisée',
        'Après 1000 lancers, la fréquence ne peut plus changer du tout',
    ];
    const nbVraies = randInt(2, 3);
    const vraies = randomChoiceMultiple(viverVraies, nbVraies);
    const fausses = randomChoiceMultiple(viverFausses, 5 - nbVraies);

    const lignes = [];
    const reponses = [];
    vraies.forEach(v => { lignes.push(v); reponses.push(0); });
    fausses.forEach(f => { lignes.push(f); reponses.push(1); });
    const indices = shuffle(lignes.map((_, i) => i));
    const ligneMel = indices.map(i => lignes[i]);
    const reponsesMel = indices.map(i => reponses[i]);

    const texte = `<p>Voici le graphique de la fréquence d'apparition d'un événement au cours de 1000 lancers successifs. Indique si chaque affirmation est vraie ou fausse.</p>${svg}{{element:0}}`;
    const payload = makeVraiFaux(ligneMel, reponsesMel, texte, opts(L_GRAPHIQUE));
    payload._meta.generatorKey = 'interpreter_graphique_stabilisation';
    return payload;
};

// ─── Registre ───
const GENERATORS = {
    comparer_frequence_probabilite_qcm_unique:         genFreqQcm,
    comparer_frequence_probabilite_texte:              genFreqTexte,
    comparer_frequence_probabilite_tableau_vf:         genFreqTableauVf,
    comparer_frequence_probabilite_texte_trou_saisie:  genFreqTexteTrouSaisie,

    estimer_succes_grand_nombre_qcm_unique: genEstimerQcm,
    estimer_succes_grand_nombre_texte:      genEstimerTexte,

    interpreter_graphique_stabilisation_texte:       genGraphiqueTexte,
    interpreter_graphique_stabilisation_tableau_vf:  genGraphiqueTableauVf,
};

// ─── Catégories ───
const FORMAT_LABELS = {
    qcm_unique: 'QCM', qcm_multiple: 'QCM Multi', texte: 'Saisie',
    texte_trou_drag: 'Drag', texte_trou_saisie: 'Saisie trous',
    relier: 'Relier', tableau_vf: 'Vrai/Faux', classer: 'Classer', ordonner: 'Ordonner',
    programme_construction: 'Construction',
};

const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'comparer_frequence_probabilite',        label: L_FREQ,      formatsList: buildFormats('comparer_frequence_probabilite') },
    { id: 'estimer_succes_grand_nombre',           label: L_ESTIMER,   formatsList: buildFormats('estimer_succes_grand_nombre') },
    { id: 'interpreter_graphique_stabilisation',   label: L_GRAPHIQUE, formatsList: buildFormats('interpreter_graphique_stabilisation') },
];

// ─── Point d'entrée ───
export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    // La clé du FORMAT résolu, TOUJOURS (pas celle de la carte posée par les
    // sous-générateurs) : c est elle que la carte d exerciseur compare pour
    // ses compteurs et que la réutilisation range (bug des compteurs muets
    // avant tout clic sur un onglet, tranché 2026-09-02).
    payload._meta.generatorKey = key;
    return payload;
};

export const _internals = {
    simplifier, fractionStr, decimalStr, pourcentageStr,
    PRENOMS, EXPERIENCES, NB_LANCERS, NB_LANCERS_GRAND,
    PROBA_CIBLES_GRAPH, svgGraphiqueStab,
};
